import { useId } from 'react'
import { Button } from './Button'
import { Dialog } from './Dialog'

/** What a confirm asks: the question, what follows from a yes, and the yes's words. */
export interface ConfirmQuestion {
  /** The question, naming the record: "Delete Acme Studio?" */
  title: string
  /** One plain line on what happens — what goes, what stays. */
  detail?: string
  /** The yes button's words: "Delete", "Void", "Revoke". */
  confirmLabel: string
}

/**
 * The ask before an ending that cannot be taken back: a small window with
 * the question, one line on what follows, and Cancel beside the yes. Reversible
 * moves keep the two-press button; this is for the ones that are not.
 * The window itself takes focus on arrival, so a stray Enter ends nothing.
 */
export function ConfirmDialog({
  open,
  question,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean
  question: ConfirmQuestion
  busy?: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  const titleId = useId()
  return (
    <Dialog open={open} onClose={onClose} labelledBy={titleId} className="sm:max-w-sm">
      <div className="rounded-3xl border bg-surface p-6 shadow-modal">
        <h2 id={titleId} className="wf-heading text-foreground">
          {question.title}
        </h2>
        {question.detail ? <p className="mt-1.5 text-ui text-muted-foreground">{question.detail}</p> : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="danger"
            busy={busy}
            onClick={() => {
              onClose()
              onConfirm()
            }}
          >
            {question.confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
